import { useState } from 'react';
import { supabase } from '../lib/supabase';

// Email and password. Anyone can create an account; it sees nothing until an
// owner approves it (docs/specs/sign-in.md).
type Mode = 'signin' | 'signup' | 'forgot';

const MIN_PASSWORD = 10;

export function SignIn() {
  const [mode, setMode] = useState<Mode>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  function go(next: Mode) {
    setMode(next);
    setError('');
    setDone('');
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'signin') {
        const { error: e } = await supabase.auth.signInWithPassword({ email, password });
        if (e) setError('Wrong email or password.');
      } else if (mode === 'signup') {
        if (password.length < MIN_PASSWORD) {
          setError(`Use at least ${MIN_PASSWORD} characters for the password.`);
          return;
        }
        const { error: e } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name.trim() }, emailRedirectTo: window.location.origin },
        });
        if (e) setError(e.message);
        else
          setDone(
            'Check your inbox and confirm your email. After that an owner approves your account and gives you access.',
          );
      } else {
        const { error: e } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (e) setError(e.message);
        else setDone('If this address has an account, a link to set a new password is on its way.');
      }
    } finally {
      setBusy(false);
    }
  }

  const title =
    mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'New password';

  return (
    <main className="grid min-h-screen place-items-center p-6">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-[32px] font-bold leading-tight tracking-tight">
          Ouradent <span className="grad-text">Lead Desk</span>
        </h1>
        <p className="mt-2 text-muted">{title}</p>

        {done ? (
          <>
            <p className="mt-6 rounded-nav bg-teal-tint p-4 text-ink-2" role="status">
              {done}
            </p>
            <button type="button" className="btn mt-4 w-full" onClick={() => go('signin')}>
              Back to sign in
            </button>
          </>
        ) : (
          <form className="mt-6 flex flex-col gap-3" onSubmit={submit}>
            {mode === 'signup' && (
              <label className="flex flex-col gap-1 text-sm text-muted" htmlFor="name">
                Your name
                <input
                  id="name"
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-nav border border-line px-4 py-3 text-base text-ink"
                />
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm text-muted" htmlFor="email">
              Email
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-nav border border-line px-4 py-3 text-base text-ink"
              />
            </label>
            {mode !== 'forgot' && (
              <label className="flex flex-col gap-1 text-sm text-muted" htmlFor="password">
                Password
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-nav border border-line px-4 py-3 text-base text-ink"
                />
              </label>
            )}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy
                ? 'One moment…'
                : mode === 'signin'
                  ? 'Sign in'
                  : mode === 'signup'
                    ? 'Create account'
                    : 'Send me a link'}
            </button>
            <div className="flex flex-wrap justify-between gap-2 text-sm">
              {mode === 'signin' ? (
                <>
                  <button
                    type="button"
                    className="text-muted underline"
                    onClick={() => go('signup')}
                  >
                    Create an account
                  </button>
                  <button
                    type="button"
                    className="text-muted underline"
                    onClick={() => go('forgot')}
                  >
                    Forgot password or first sign-in
                  </button>
                </>
              ) : (
                <button type="button" className="text-muted underline" onClick={() => go('signin')}>
                  Back to sign in
                </button>
              )}
            </div>
          </form>
        )}

        {error && (
          <p className="mt-4 rounded-nav bg-red-tint p-3 text-sm text-red" role="alert">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}

/** Shown after an invite or a reset link: the person chooses a password. */
export function SetPassword({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    const { error: e } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (e) setError(e.message);
    else onDone();
  }

  return (
    <main className="grid min-h-screen place-items-center p-6">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-[28px] font-bold leading-tight tracking-tight">Choose a password</h1>
        <p className="mt-2 text-muted">You will use it with your email to sign in.</p>
        <form className="mt-6 flex flex-col gap-3" onSubmit={submit}>
          <label className="flex flex-col gap-1 text-sm text-muted" htmlFor="newpw">
            New password
            <input
              id="newpw"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-nav border border-line px-4 py-3 text-base text-ink"
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            Save password
          </button>
        </form>
        {error && (
          <p className="mt-4 rounded-nav bg-red-tint p-3 text-sm text-red" role="alert">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
